import {
  differenceInDays,
  differenceInSeconds,
  format,
  isToday,
  isTomorrow,
  isYesterday,
} from 'date-fns';
import i18n from '@/i18n';

/**
 * Formats a countdown display from a target date
 * Returns formats like: "2-Days", "1-Day", "06:42:07", "42:07", "00:07"
 * If >= 24 hours, shows only days (e.g., "2-Days")
 * If < 24 hours, shows detailed countdown (e.g., "06:42:07")
 */
export const formatCountdown = (targetDate: Date): string => {
  const now = new Date();
  const totalSeconds = differenceInSeconds(targetDate, now);

  if (totalSeconds <= 0) {
    return '00:00';
  }

  const days = Math.floor(totalSeconds / (24 * 60 * 60));
  const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
  const seconds = totalSeconds % 60;

  // If time span is >= 24 hours, just show days
  if (days > 0) {
    return days === 1 ? i18n.t('date.oneDayHyphen') : i18n.t('date.daysHyphen', { count: days });
  }

  // Otherwise show detailed countdown
  const parts: string[] = [];

  if (hours > 0) {
    parts.push(`${hours.toString().padStart(2, '0')}`);
  }
  parts.push(`${minutes.toString().padStart(2, '0')}`);
  parts.push(`${seconds.toString().padStart(2, '0')}`);

  return parts.join(':');
};

/**
 * Formats a relative time display
 * Returns formats like: "2 Days", "Tomorrow", "Today", etc.
 */
export const formatRelativeTime = (targetDate: Date): string => {
  // Validate date
  if (!targetDate || isNaN(targetDate.getTime())) {
    return i18n.t('date.tbd');
  }

  if (isToday(targetDate)) {
    return i18n.t('date.today');
  }

  if (isTomorrow(targetDate)) {
    return i18n.t('date.tomorrow');
  }

  if (isYesterday(targetDate)) {
    return i18n.t('date.yesterday');
  }

  const now = new Date();
  const diffInDays = differenceInDays(targetDate, now);

  // Additional check for NaN result
  if (isNaN(diffInDays)) {
    return i18n.t('date.tbd');
  }

  if (diffInDays > 0) {
    return i18n.t('date.days', { count: diffInDays });
  } else {
    return i18n.t('date.daysAgo', { count: Math.abs(diffInDays) });
  }
};

/**
 * Formats a date for daily breakdown display
 * Returns formats like: "Jan 24", "Feb 01", etc.
 */
export const formatDayMonth = (date: Date): string => {
  return format(date, 'MMM dd');
};

/**
 * Formats a full date display
 * Returns formats like: "SAT FEB 01", "MON MAR 15", etc.
 */
export const formatGameTitle = (date: Date): string => {
  return format(date, 'EEE MMM dd').toUpperCase();
};

/**
 * Formats time remaining until a date
 * Returns different formats based on time remaining
 */
export const formatTimeRemaining = (targetDate: Date): string => {
  const now = new Date();

  // If date is in the past, return "Live"
  if (targetDate < now) {
    return i18n.t('game.status.live');
  }

  return formatCountdown(targetDate);
};
